import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSiteUrl } from "./SiteConfig";

/**
 * Sets this page's <title>, <meta name="description">,
 * <link rel="canonical"> and the Open Graph share tags (og:title,
 * og:description, og:url) together. WhatsApp, LinkedIn and Facebook read the
 * og: tags from the prerendered HTML, so without this every page shared as
 * the homepage. The description tag already exists
 * in public/index.html (one, global) -- this updates its content per
 * page rather than creating a second one. The canonical link doesn't
 * exist by default, so this creates it once and updates its href on
 * every route change.
 *
 * @param {string} title - full <title> text, e.g. "About Us | TZ Creation"
 * @param {string} description - ~150-160 char meta description
 * @param {string} [path] - route path for the canonical URL, e.g. "/about".
 *   Defaults to the current location so dynamic (:id) routes work with no
 *   extra argument. Pass an explicit path when the visible URL shouldn't
 *   be its own canonical (not needed anywhere yet).
 * @param {boolean} [noindex] - adds <meta name="robots" content="noindex">
 *   for pages that shouldn't be indexed (currently just the 404 page).
 */
const usePageMeta = ({ title, description, path, noindex = false }) => {
  const location = useLocation();

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      const tag = document.querySelector('meta[name="description"]');
      if (tag) {
        tag.setAttribute("content", description);
      }
    }

    const canonicalHref = `${getSiteUrl()}${path ?? location.pathname}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalHref);

    // Open Graph tags live in public/index.html with homepage values; update
    // them per page (create the tag if it is somehow missing).
    const setOg = (property, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setOg("og:title", title);
    setOg("og:description", description);
    setOg("og:url", canonicalHref);

    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex");
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, path, noindex, location.pathname]);
};

export default usePageMeta;
